import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import {PRIMARY_COLOR, PRIMARY_COLOR_LIGHT} from "../../contants/Colors";
import ResponsivePie from "./ResponsivePie";
import ResponsiveBarChart from "./ResponsiveBarChart";

const FILTERS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thisWeek', label: 'This Week' },
  { id: 'lastWeek', label: 'Last Week' },
  { id: 'custom', label: 'Custom Range' },
  { id: '', label: 'All Time' },
];

const EMOJI_MAP = { angry: '😠', sad: '😢', neutral: '😐', happy: '😊', excited: '🤩' };
const COLORS = { angry: '#ef4444', sad: '#f97316', neutral: '#94a3b8', happy: '#22c55e', excited: '#8b5cf6' };
const API_UPLOAD_BASE = process.env.REACT_APP_API_UPLOAD_URL;

const ANALYSIS_STATUS = {
  pending:    { label: '⏳ Pending analysis',    bg: '#fef9c3', color: '#92400e' },
  processing: { label: '🔄 Analysing audio…',    bg: '#dbeafe', color: '#1d4ed8' },
  done:       { label: '✅ Analysis complete',    bg: '#dcfce7', color: '#15803d' },
  failed:     { label: '❌ Analysis failed',      bg: '#fee2e2', color: '#b91c1c' },
};

const FLAG_MAP = {
  english:    { label: 'English',    bg: '#dbeafe', color: '#1d4ed8' },
  urdu:       { label: 'Urdu',       bg: '#fce7f3', color: '#9d174d' },
  arabic:     { label: 'Arabic',     bg: '#fef9c3', color: '#92400e' },
  french:     { label: 'French',     bg: '#dbeafe', color: '#1d4ed8' },
  spanish:    { label: 'Spanish',    bg: '#dcfce7', color: '#15803d' },
  german:     { label: 'German',     bg: '#f3f4f6', color: '#374151' },
  hindi:      { label: 'Hindi',      bg: '#fde68a', color: '#92400e' },
  chinese:    { label: 'Chinese',    bg: '#fee2e2', color: '#991b1b' },
  japanese:   { label: 'Japanese',   bg: '#ede9fe', color: '#5b21b6' },
  portuguese: { label: 'Portuguese', bg: '#dcfce7', color: '#15803d' },
  russian:    { label: 'Russian',    bg: '#fee2e2', color: '#991b1b' },
  turkish:    { label: 'Turkish',    bg: '#fef9c3', color: '#92400e' },
};

// ── Audio response detail ─────────────────────────────────────────────────
function AudioResponseDetail({ r, onRetry }) {
  const [retrying, setRetrying] = useState(false);
  const status  = ANALYSIS_STATUS[r.analysisStatus] || ANALYSIS_STATUS.pending;
  const langKey = r.detectedLanguage?.toLowerCase();
  const langInfo = FLAG_MAP[langKey] || { label: r.detectedLanguage || 'Unknown', bg: '#f1f5f9', color: '#475569' };

  const handleRetry = async (e) => {
    e.stopPropagation();
    setRetrying(true);
    try { await api.post(`/response/retry-analysis/${r._id}`); onRetry(); }
    catch {} finally { setRetrying(false); }
  };

  return (
      <div>
        {r.audioFile && (
            <audio controls src={`${API_UPLOAD_BASE}${r.audioFile}`} style={{ width: '100%', borderRadius: 8, marginBottom: 12 }} />
        )}
        {r.audioDuration && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Duration: {r.audioDuration}s</p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, background: status.bg, color: status.color }}>
          {status.label}
        </span>
          {r.analysisStatus === 'failed' && (
              <button className="btn btn-outline btn-sm" onClick={handleRetry} disabled={retrying} style={{ fontSize: 11 }}>
                {retrying ? 'Retrying…' : '🔄 Retry Analysis'}
              </button>
          )}
        </div>
        {r.detectedLanguage && r.detectedLanguage !== 'unknown' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8, background: langInfo.bg, color: langInfo.color, letterSpacing: '.02em' }}>
            {langInfo.label}
          </span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>detected</span>
            </div>
        )}
        {r.overallSentiment && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>{EMOJI_MAP[r.overallSentiment]}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Sentiment: <strong style={{ color: COLORS[r.overallSentiment], textTransform: 'capitalize' }}>{r.overallSentiment}</strong>
          </span>
            </div>
        )}
        {r.transcript && (
            <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)', marginBottom: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: langInfo.bg, color: langInfo.color, marginRight: 6 }}>
              {langInfo.label}
            </span>
                Original transcript
              </p>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>"{r.transcript}"</p>
            </div>
        )}
        {r.transcriptEnglish && r.transcriptEnglish !== r.transcript && (
            <div style={{ background: '#eff6ff', borderRadius: 8, padding: '12px 14px', border: '1px solid #bfdbfe', marginBottom: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#1d4ed8', marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#dbeafe', color: '#1d4ed8', marginRight: 6 }}>English</span>
                translation
              </p>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>"{r.transcriptEnglish}"</p>
            </div>
        )}
        {r.analysisStatus === 'failed' && r.analysisError && (
            <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>Error: {r.analysisError}</p>
        )}
      </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function ViewResponses({ highlightResponseId, onClearHighlight }) {
  const [responses, setResponses]         = useState([]);
  const [summary, setSummary]             = useState(null);
  const [filter, setFilter]               = useState('today');
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  const [locationId, setLocationId]       = useState('all');
  const [locations, setLocations]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [expanded, setExpanded]           = useState(null);
  const [sentimentFilter, setSentimentFilter] = useState(null); // ← internal filter

  const load = async () => {
    setLoading(true); setError('');
    try {
      let url = `/response/tenant?filter=${filter}`;
      if (filter === 'custom') {
        if (!startDate || !endDate) { setLoading(false); return; }
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      if (locationId && locationId !== 'all') url += `&locationId=${locationId}`;
      const { data } = await api.get(url);
      setResponses(data.data);
      setSummary({ total: data.total, sentimentCounts: data.sentimentCounts });
    } catch { setError('Failed to load responses'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filter, startDate, endDate, locationId]);

  // ── Notification deep-link ────────────────────────────────────────────
  useEffect(() => {
    if (!highlightResponseId) return;
    setLoading(true);
    api.get(`/response/single/${highlightResponseId}`)
        .then(r => {
          setResponses([r.data.data]);
          setExpanded(highlightResponseId);
          setSummary(null);
          setSentimentFilter(null); // clear any sentiment filter when viewing single
          setLoading(false);
        })
        .catch(() => load());
  }, [highlightResponseId]);

  // ── Filtered list ─────────────────────────────────────────────────────
  const displayedResponses = sentimentFilter
      ? responses.filter(r => r.overallSentiment === sentimentFilter)
      : responses;

  const pieData = summary
      ? Object.entries(summary.sentimentCounts).filter(([, v]) => v > 0)
          .filter(([k]) => !sentimentFilter || k === sentimentFilter)
          .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, key: k }))
      : [];

  const barData = summary
      ? Object.entries(summary.sentimentCounts)
          .filter(([k]) => !sentimentFilter || k === sentimentFilter)
          .map(([k, v]) => ({
            name: `${EMOJI_MAP[k]} ${k.charAt(0).toUpperCase() + k.slice(1)}`,
            count: v, key: k,
          }))
      : [];

  // Handle pie/bar click
  const handleChartClick = (data) => {
    if (!data) return;
    const key = data.key || data.activePayload?.[0]?.payload?.key;
    if (key) setSentimentFilter(prev => prev === key ? null : key);
  };

  return (
      <div>
        <div className="page-header">
          <h3 style={{ marginBottom: 4 }}>Feedback Responses</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Your customers spoke. Here's what they said.</p>
        </div>

        {/* ── Notification deep-link banner ── */}
        {highlightResponseId && (
            <div style={{
              background: PRIMARY_COLOR_LIGHT, border: '1px solid #FF2E5844',
              borderRadius: 10, padding: '10px 16px', marginBottom: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
          <span style={{ fontSize: 13, color: PRIMARY_COLOR, fontWeight: 600 }}>
            🔔 Viewing flagged response
          </span>
              <button
                  onClick={() => { onClearHighlight?.(); load(); }}
                  style={{ background: 'none', border: 'none', color: PRIMARY_COLOR, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                ✕ View All
              </button>
            </div>
        )}

        {/* ── Sentiment filter banner ── */}
        {sentimentFilter && !highlightResponseId && (
            <div style={{
              background: COLORS[sentimentFilter] + '15',
              border: `1px solid ${COLORS[sentimentFilter]}40`,
              borderRadius: 10, padding: '10px 16px', marginBottom: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: COLORS[sentimentFilter] }}>
            {EMOJI_MAP[sentimentFilter]} Showing {sentimentFilter} responses · {displayedResponses.length} found
          </span>
              <button
                  onClick={() => setSentimentFilter(null)}
                  style={{ background: 'none', border: 'none', color: COLORS[sentimentFilter], cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                ✕ Show all
              </button>
            </div>
        )}

        {/* ── Filters — hidden in highlight mode ── */}
        {!highlightResponseId && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                {FILTERS.map(f => (
                    <button
                        key={f.id}
                        className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-outline'}`}
                        style={{ border: `1px solid ${filter === f.id ? PRIMARY_COLOR : ''}` }}
                        onClick={() => { setFilter(f.id); setSentimentFilter(null); }}
                    >
                      {f.label}
                    </button>
                ))}
              </div>

              {locations.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>📍 Location:</span>
                    <button className={`btn btn-sm ${locationId === 'all' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLocationId('all')}>
                      All Locations
                    </button>
                    {locations.map(loc => (
                        <button
                            key={loc._id}
                            className={`btn btn-sm ${locationId === loc._id ? 'btn-primary' : 'btn-outline'}`}
                            style={{ border: `1px solid ${locationId === loc._id ? PRIMARY_COLOR : ''}` }}
                            onClick={() => setLocationId(loc._id)}
                        >
                          {loc.name}{loc.city ? ` · ${loc.city}` : ''}
                        </button>
                    ))}
                  </div>
              )}

              {filter === 'custom' && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Start Date</label>
                      <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>End Date</label>
                      <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                  </div>
              )}
            </>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        {/* ── Summary cards — clickable to filter ── */}
        {summary && !highlightResponseId && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
              <div
                  className="card"
                  style={{ flex: '1 1 120px', textAlign: 'center', padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', cursor: sentimentFilter ? 'pointer' : 'default', opacity: sentimentFilter ? 0.6 : 1, transition: 'opacity .2s' }}
                  onClick={() => setSentimentFilter(null)}
                  title={sentimentFilter ? 'Clear filter' : ''}
              >
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{summary.total}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Total</div>
              </div>
              {Object.entries(summary.sentimentCounts).map(([key, val]) => (
                  <div
                      key={key}
                      className="card"
                      onClick={() => setSentimentFilter(prev => prev === key ? null : key)}
                      style={{
                        flex: '1 1 100px', textAlign: 'center', padding: '16px 12px', cursor: 'pointer',
                        border: sentimentFilter === key ? `2px solid ${COLORS[key]}` : undefined,
                        background: sentimentFilter === key ? COLORS[key] + '12' : undefined,
                        opacity: sentimentFilter && sentimentFilter !== key ? 0.45 : 1,
                        transition: 'all .2s', transform: sentimentFilter === key ? 'translateY(-2px)' : 'none',
                      }}
                      title={`Filter by ${key}`}
                  >
                    <div style={{ fontSize: 22 }}>{EMOJI_MAP[key]}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: COLORS[key] }}>{val}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{key}</div>
                  </div>
              ))}
            </div>
        )}

        {/* ── Charts — clickable to filter ── */}
        {summary && summary.total > 0 && !highlightResponseId && (
            <div className="grid-2 mb-3">
              <div className="card">
                <h4 style={{ marginBottom: 4, fontSize: 15 }}>Sentiment Distribution</h4>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>Click a slice to filter</p>
                <ResponsivePie
                    pieData={pieData}
                    COLORS={COLORS}
                    onSliceClick={(entry) => setSentimentFilter(prev => prev === entry.key ? null : entry.key)}
                    activeKey={sentimentFilter}
                />
              </div>
              <div className="card">
                <h4 style={{ marginBottom: 4, fontSize: 15 }}>Response Counts</h4>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>Click a bar to filter</p>
                <ResponsiveBarChart
                    barData={barData}
                    COLORS={COLORS}
                    onBarClick={(entry) => setSentimentFilter(prev => prev === entry.key ? null : entry.key)}
                    activeKey={sentimentFilter}
                />
              </div>
            </div>
        )}

        {/* ── Response list ── */}
        {loading ? <div className="spinner" /> : (
            <div>
              {displayedResponses.length === 0 ? (
                  <div className="card text-center" style={{ padding: 48, color: 'var(--text-muted)' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>{sentimentFilter ? EMOJI_MAP[sentimentFilter] : '📭'}</div>
                    <p>{sentimentFilter ? `No ${sentimentFilter} responses for this period.` : 'No responses for this period.'}</p>
                    {sentimentFilter && (
                        <button className="btn btn-outline btn-sm" style={{ marginTop: 12 }} onClick={() => setSentimentFilter(null)}>
                          Show all responses
                        </button>
                    )}
                  </div>
              ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {displayedResponses.map(r => (
                        <div
                            key={r._id}
                            className="card response-card"
                            style={{
                              padding: '16px 20px',
                              cursor: 'pointer',
                              border: highlightResponseId === r._id ? `2px solid ${PRIMARY_COLOR}` : undefined,
                              background: highlightResponseId === r._id ? PRIMARY_COLOR_LIGHT : undefined,
                            }}
                            onClick={() => setExpanded(expanded === r._id ? null : r._id)}
                        >
                          <style>{`
                    .response-container { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                    .main-content-group { display: contents; }
                    .emoji-section { order: 1; flex-shrink: 0; font-size: 24px; }
                    .text-section  { order: 2; flex: 1; min-width: 0; }
                    .badge-section { order: 3; display: flex; gap: 8px; align-items: center; }
                    .arrow-section { order: 4; font-size: 12px; color: var(--text-muted); flex-shrink: 0; }
                    @media (max-width: 600px) {
                      .response-container { align-items: flex-start; }
                      .main-content-group { display: flex; flex: 1 1 100%; align-items: flex-start; gap: 12px; order: 1; }
                      .emoji-section { order: 1; }
                      .text-section  { order: 2; flex: 1; }
                      .arrow-section { order: 3; margin-top: 4px; }
                      .badge-section { width: 100%; order: 2; padding-left: 36px; margin-top: 4px; }
                    }
                  `}</style>

                          <div className="response-container">
                            <div className="main-content-group">
                      <span className="emoji-section">
                        {r.feedbackType === 'audio' ? '🎙️' : (EMOJI_MAP[r.overallSentiment] || '📝')}
                      </span>
                              <div className="text-section">
                                <div style={{ fontWeight: 600, fontSize: 14 }}>
                                  {r.feedbackTaker?.name || r.customerName || 'Unknown staff'} &nbsp;·&nbsp;
                                  <span className="text-muted" style={{ fontWeight: 400 }}>
                            Bill: {r.billId || 'N/A'}
                                    {r.locationName && <span> &nbsp;·&nbsp; 📍 {r.locationName}</span>}
                          </span>
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                  {r.feedbackTaker && <span>{'👤 ' + r.feedbackTaker?.name}&nbsp;·&nbsp;</span>}
                                  {new Date(r.createdAt).toLocaleString()}<br/>
                                  {highlightResponseId === r._id && (
                                      <span style={{ color: PRIMARY_COLOR, fontWeight: 600 }}>🔔 From notification</span>
                                  )}
                                </div>
                              </div>
                              <span className="arrow-section">{expanded === r._id ? '▲' : '▼'}</span>
                            </div>

                            <div className="badge-section">
                              <span className={`badge badge-${r.feedbackType === 'audio' ? 'info' : 'gray'}`}>{r.feedbackType}</span>
                              {r.feedbackType === 'audio' && !r.overallSentiment && r.analysisStatus && (
                                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: ANALYSIS_STATUS[r.analysisStatus]?.bg, color: ANALYSIS_STATUS[r.analysisStatus]?.color, fontWeight: 600 }}>
                          {r.analysisStatus === 'pending' ? '⏳ Pending' : r.analysisStatus === 'processing' ? '🔄 Analysing' : r.analysisStatus === 'failed' ? '❌ Failed' : ''}
                        </span>
                              )}
                              {r.overallSentiment && (
                                  <span className="badge" style={{ background: COLORS[r.overallSentiment] + '22', color: COLORS[r.overallSentiment] }}>
                          {EMOJI_MAP[r.overallSentiment]} {r.overallSentiment}
                        </span>
                              )}
                            </div>
                          </div>

                          {expanded === r._id && (
                              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                                {r.feedbackType === 'audio' ? (
                                    <AudioResponseDetail r={r} onRetry={load} />
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                      {r.answers.map((a, i) => (
                                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 20 }}>Q{i + 1}</span>
                                            <span style={{ flex: 1, fontSize: 13 }}>{a.questionText}</span>
                                            <span style={{ fontSize: 22 }}>{EMOJI_MAP[a.emojiLabel]}</span>
                                            <span style={{ fontSize: 12, color: COLORS[a.emojiLabel], fontWeight: 600, textTransform: 'capitalize' }}>{a.emojiLabel}</span>
                                          </div>
                                      ))}
                                      {r.averageScore && (
                                          <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'right' }}>
                                            Avg score: <strong>{r.averageScore.toFixed(1)} / 5</strong>
                                          </div>
                                      )}
                                    </div>
                                )}
                              </div>
                          )}
                        </div>
                    ))}
                  </div>
              )}
            </div>
        )}
      </div>
  );
}